// deps
import React, { useCallback, useEffect, useRef, useState } from "react";

//internal deps
import { getFormattedNumber } from "@super-ui/helpers/String";

//styles
import { DiscountCounterWrapper, DiscountText, HeroString } from "./styles";

const animationInterval = 15;
const maxAnimationDuration = 2000;

interface CartSavingsBannerOneLineProps {
    totalDiscount: number;
}

const CartSavingsBannerOneLineDiscountCounter: (props: CartSavingsBannerOneLineProps) => JSX.Element = ({
    totalDiscount,
}: CartSavingsBannerOneLineProps) => {
    const heroRef = useRef<HTMLSpanElement>(null);
    const [heroCount, setHeroCount] = useState(totalDiscount);
    const [isUpdating, setIsUpdating] = useState(false);

    useEffect(() => {
        let start = heroCount;
        const end = totalDiscount;

        if (start === end) {
            setIsUpdating(false);
            return;
        }

        start = Math.round(start);
        if (isNaN(end) || start > end) {
            setIsUpdating(false);
            setHeroCount(end);
            return;
        }

        setIsUpdating(true);
        const absoluteChange = Math.abs(end - start);
        let step = 1;
        if (absoluteChange > 200) {
            step = 10;
        }

        const timer = setInterval(() => {
            start += step;
            setHeroCount(start);

            if (start === end) {
                setIsUpdating(false);
                clearInterval(timer);
            }
        }, Math.min(animationInterval, maxAnimationDuration / absoluteChange));
        return (): void => {
            clearInterval(timer);
        };
    }, [heroCount, totalDiscount]);

    const renderCounter = useCallback(
        (isShadow: boolean) => {
            return (
                <HeroString ref={!isShadow ? heroRef : null} isShadow={isShadow} isUpdating={isUpdating}>
                    <DiscountText>{getFormattedNumber(isShadow ? totalDiscount : heroCount)}</DiscountText>
                </HeroString>
            );
        },
        [totalDiscount, heroCount],
    );

    return (
        <DiscountCounterWrapper>
            {renderCounter(false)}
            {/* Generating a transparent element to avoid flickering during animation */}
            {renderCounter(true)}
        </DiscountCounterWrapper>
    );
};

export default React.memo(CartSavingsBannerOneLineDiscountCounter);
